import { useState } from 'react';
import useSWR from 'swr';
import { Link } from 'react-router-dom';
import { adminApi } from '../../lib/api';
import { useConfirm } from '../../components/ConfirmDialog';
import type { AdminUser, AdminMembership, AdminApiKey } from '../../types';

const PAGE_SIZE = 50;

export function UsersPage() {
  const [search, setSearch] = useState('');
  const [skip, setSkip] = useState(0);
  const [selected, setSelected] = useState<AdminUser | null>(null);
  const confirm = useConfirm();

  const { data, mutate } = useSWR(
    ['admin/users', search, skip],
    () => adminApi.listUsers({ search, limit: PAGE_SIZE, skip }),
    { keepPreviousData: true }
  );

  const handleVerify = async (user: AdminUser) => {
    const ok = await confirm({
      title: `Verify ${user.email}?`,
      message: 'They will be able to log in immediately.',
      confirmLabel: 'Verify',
    });
    if (!ok) return;
    await adminApi.verifyUser(user._id);
    mutate();
    setSelected(null);
  };

  const handleToggleAdmin = async (user: AdminUser) => {
    const target = !user.isAlphyAdmin;
    const ok = await confirm({
      title: target ? `Grant Alphy admin to ${user.email}?` : `Revoke Alphy admin from ${user.email}?`,
      message: target
        ? 'They will be able to sign in to the HarmCheck Console and manage users, organizations, and API keys.'
        : 'They will lose access to the HarmCheck Console immediately.',
      confirmLabel: target ? 'Grant admin' : 'Revoke admin',
      danger: !target,
    });
    if (!ok) return;
    await adminApi.setUserAdmin(user._id, target);
    mutate();
    setSelected(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-gray-100">Users</h1>
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setSkip(0); }}
          placeholder="Search by email or name…"
          className="bg-gray-900 border border-gray-800 rounded px-3 py-1.5 text-sm text-gray-100 w-72"
        />
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800 text-gray-400 text-left">
              <th className="px-4 py-2 font-medium">Email</th>
              <th className="px-4 py-2 font-medium">Name</th>
              <th className="px-4 py-2 font-medium">Verified</th>
              <th className="px-4 py-2 font-medium">Admin</th>
              <th className="px-4 py-2 font-medium">SSO</th>
              <th className="px-4 py-2 font-medium">Last login</th>
              <th className="px-4 py-2 font-medium">Created</th>
            </tr>
          </thead>
          <tbody>
            {data?.users.map((u) => (
              <tr
                key={u._id}
                className="border-b border-gray-800 hover:bg-gray-800/40 cursor-pointer"
                onClick={() => setSelected(u)}
              >
                <td className="px-4 py-2 text-gray-100">{u.email}</td>
                <td className="px-4 py-2 text-gray-300">{u.name}</td>
                <td className="px-4 py-2">{u.isVerified ? <Pill ok>verified</Pill> : <Pill>unverified</Pill>}</td>
                <td className="px-4 py-2">{u.isAlphyAdmin ? <Pill warn>admin</Pill> : <span className="text-gray-600">—</span>}</td>
                <td className="px-4 py-2 text-gray-400">
                  {u.hasGoogle && <span className="mr-2">Google</span>}
                  {u.hasMicrosoft && <span>Microsoft</span>}
                </td>
                <td className="px-4 py-2 text-gray-500 text-xs">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : '—'}</td>
                <td className="px-4 py-2 text-gray-500 text-xs">{new Date(u.createdAt).toLocaleDateString()}</td>
              </tr>
            ))}
            {data && data.users.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No users</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-gray-400">
          <span>Showing {skip + 1}–{Math.min(skip + PAGE_SIZE, data.total)} of {data.total}</span>
          <div className="flex gap-2">
            <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))} className="px-3 py-1 rounded bg-gray-800 disabled:opacity-30">Prev</button>
            <button disabled={skip + PAGE_SIZE >= data.total} onClick={() => setSkip(skip + PAGE_SIZE)} className="px-3 py-1 rounded bg-gray-800 disabled:opacity-30">Next</button>
          </div>
        </div>
      )}

      {selected && (
        <UserDrawer
          user={selected}
          onClose={() => setSelected(null)}
          onVerify={() => handleVerify(selected)}
          onToggleAdmin={() => handleToggleAdmin(selected)}
        />
      )}
    </div>
  );
}

function Pill({ children, ok, warn }: { children: React.ReactNode; ok?: boolean; warn?: boolean }) {
  const cls = ok
    ? 'bg-emerald-900/40 text-emerald-300'
    : warn
    ? 'bg-yellow-900/40 text-yellow-300'
    : 'bg-gray-800 text-gray-400';
  return <span className={`text-xs px-2 py-0.5 rounded ${cls}`}>{children}</span>;
}

function UserDrawer({
  user,
  onClose,
  onVerify,
  onToggleAdmin,
}: {
  user: AdminUser;
  onClose: () => void;
  onVerify: () => void;
  onToggleAdmin: () => void;
}) {
  const { data: memberships } = useSWR(['admin/users', user._id, 'memberships'], () =>
    adminApi.getUserMemberships(user._id)
  );
  const { data: keys } = useSWR(['admin/users', user._id, 'api-keys'], () =>
    adminApi.getUserApiKeys(user._id)
  );

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-black/40" />
      <div
        className="w-[28rem] bg-gray-900 border-l border-gray-800 p-6 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-100">{user.name}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-200">×</button>
        </div>
        <div className="text-sm text-gray-400 space-y-1 mb-6">
          <div>{user.email}</div>
          <div className="text-xs">id: <code>{user._id}</code></div>
          <div className="text-xs">created {new Date(user.createdAt).toLocaleString()}</div>
        </div>

        <div className="flex gap-2 mb-6">
          {!user.isVerified && (
            <button onClick={onVerify} className="px-3 py-1.5 text-sm rounded bg-emerald-900/50 text-emerald-200 border border-emerald-800 hover:bg-emerald-900">
              Verify
            </button>
          )}
          <button onClick={onToggleAdmin} className="px-3 py-1.5 text-sm rounded bg-yellow-900/40 text-yellow-200 border border-yellow-800 hover:bg-yellow-900/60">
            {user.isAlphyAdmin ? 'Revoke admin' : 'Grant admin'}
          </button>
        </div>

        <Section title="Memberships">
          {memberships?.memberships.length === 0 && <Empty>No org memberships</Empty>}
          <ul className="space-y-1.5">
            {memberships?.memberships.map((m: AdminMembership) => (
              <li key={m.id} className="flex items-center justify-between text-sm">
                <Link to={`/admin/orgs/${m.organization?._id || ''}`} className="text-blue-400 hover:underline">
                  {m.organization?.name || '(deleted)'}
                </Link>
                <span className="text-xs text-gray-500">{m.role}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="API keys">
          {keys?.apiKeys.length === 0 && <Empty>No API keys</Empty>}
          <ul className="space-y-1.5">
            {keys?.apiKeys.map((k: AdminApiKey) => (
              <li key={k._id} className="flex items-center justify-between text-sm">
                <code className="text-xs text-gray-300">{k.prefix}…</code>
                <span className="text-xs text-gray-500">
                  {k.environment} · {k.isActive ? 'active' : 'revoked'}
                </span>
              </li>
            ))}
          </ul>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-6">
      <h3 className="text-xs uppercase tracking-wide text-gray-500 mb-2">{title}</h3>
      {children}
    </div>
  );
}
function Empty({ children }: { children: React.ReactNode }) {
  return <div className="text-xs text-gray-600 italic">{children}</div>;
}
